import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaTag } from 'react-icons/fa6';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { FaExclamationTriangle } from 'react-icons/fa';
import { formatCurrency } from '../../../../utils/currency';

interface LineItemsBreakdownProps {
  lineItems: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    multipliers?: Array<{ value: number; unit: string }>;
    lineTotal: number;
  }>;
  adjustment?: {
    type: string;
    value: number;
    mode?: 'percent' | 'fixed';
    reason?: string;
  };
  addonSubtotal?: number;
  additionalServices?: Array<{
    id: string;
    serviceItem: string;
    price: number;
    images?: string;
  }>;
  quotedPrice?: number;
}

/**
 * LineItemsBreakdown — Enhanced expandable itemized table with modern badges & breakdown
 */
const LineItemsBreakdown = ({
  lineItems,
  adjustment,
  addonSubtotal = 0,
  additionalServices = [],
  quotedPrice,
}: LineItemsBreakdownProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(lineItems.length < 8);
  const shouldCollapse = lineItems.length >= 8;

  const subtotal = lineItems.reduce((sum, item) => {
    return typeof item.lineTotal === 'number' ? sum + item.lineTotal : sum;
  }, 0);
  const hasUnpricedLineItem = lineItems.some(
    (item) => typeof item.lineTotal !== 'number'
  );

  const isPercentAdjustment = adjustment?.mode === 'percent';

  // Actual money value of the adjustment
  const adjustmentAmount = adjustment
    ? isPercentAdjustment
      ? (subtotal * adjustment.value) / 100
      : adjustment.value
    : 0;

  const adjustmentDelta = adjustment
    ? adjustment.type === 'discount'
      ? -Math.abs(adjustmentAmount)
      : Math.abs(adjustmentAmount)
    : 0;

  const adjustedTotal = Math.max(0, subtotal + adjustmentDelta);
  const grandTotal = Math.max(0, adjustedTotal + addonSubtotal);
  const finalQuotedPrice = quotedPrice ?? grandTotal;

  return (
    <div className="bg-slate-50/70 rounded-xl p-3 border border-slate-200/60 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold tracking-wider uppercase text-slate-500 flex items-center gap-1.5">
          <FaTag className="text-primary/70" size={12} />
          {t('jobs.line_items', 'Line Items')}
        </span>
        {shouldCollapse && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-primary hover:text-[#007b97] font-semibold flex items-center gap-1 transition-colors"
          >
            {isExpanded ? (
              <>
                {t('jobs.hide_breakdown', 'Hide breakdown')}{' '}
                <MdExpandLess size={16} />
              </>
            ) : (
              <>
                {t('jobs.show_breakdown', 'Show breakdown')}{' '}
                <MdExpandMore size={16} />
              </>
            )}
          </button>
        )}
      </div>

      {isExpanded && (
        <div className="space-y-2.5 pt-1">
          {lineItems.map((item, idx) => (
            <div
              key={idx}
              className="flex justify-between items-start gap-3 p-2 rounded-lg bg-white border border-slate-100 shadow-2xs"
            >
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-slate-700 truncate">
                  {item.description}
                </p>
                <p className="text-xs text-slate-400 mt-0.5">
                  {item.quantity} × {formatCurrency(item.unitPrice)}
                  {item.multipliers && item.multipliers.length > 0
                    ? item.multipliers.map((m) => (
                        <span
                          key={`${m.value}-${m.unit}`}
                          className="ml-1 text-slate-500"
                        >
                          × {m.value} {m.unit}
                        </span>
                      ))
                    : null}
                </p>
              </div>
              <p className="font-semibold text-xs text-slate-800 shrink-0">
                {typeof item.lineTotal === 'number'
                  ? formatCurrency(item.lineTotal)
                  : '—'}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Totals Section */}
      <div className="border-t border-slate-200/80 pt-3 space-y-2 text-sm">
        <div className="flex text-xs justify-between text-slate-600">
          <span>{t('jobs.subtotal', 'Subtotal')}</span>
          <span className="font-xs text-slate-800">
            {formatCurrency(subtotal)}
          </span>
        </div>

        {hasUnpricedLineItem && (
          <p className="text-xs text-amber-600 flex items-center gap-1">
            <FaExclamationTriangle size={11} />
            {t(
              'jobs.pending_pricing_note',
              'Some line items are pending pricing and are not yet included in these totals.'
            )}
          </p>
        )}

        {addonSubtotal > 0 && additionalServices.length > 0 && (
          <div className="flex text-xs justify-between text-slate-600">
            <span>{t('jobs.addon_services', 'Add-on Services')}</span>
            <span className="font-xstext-slate-800">
              {formatCurrency(addonSubtotal)}
            </span>
          </div>
        )}

        {adjustment && (
          <div className="flex justify-between text-slate-600">
            <div className="flex text-xs items-center gap-1.5">
              <span className="capitalize">{adjustment.type}</span>
              {adjustment.reason && (
                <span className="text-xs text-slate-400">
                  ({adjustment.reason})
                </span>
              )}
            </div>
            <span
              className={`font-xs ${
                adjustment.type === 'discount'
                  ? 'text-emerald-600'
                  : 'text-slate-800'
              }`}
            >
              {adjustment.type === 'discount' ? '−' : '+'}
              {formatCurrency(Math.abs(adjustmentAmount))}
              {isPercentAdjustment && ` (${adjustment.value}%)`}
            </span>
          </div>
        )}

        {addonSubtotal > 0 && (
          <div className="flex justify-between border-t border-slate-200/80 pt-2 font-semibold text-slate-800">
            <span>{t('jobs.total', 'Total')}</span>
            <span className="text-primary">{formatCurrency(grandTotal)}</span>
          </div>
        )}

        {/* Quoted Price Banner */}
        <div className="flex justify-between items-center bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-xl px-4 py-3 border border-primary/20 shadow-2xs mt-2">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-primary text-white">
              <FaTag size={14} />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {t('jobs.quoted_price', 'Quoted Price')}
              </p>
              <p className="text-xs text-slate-400">
                All taxes & fees included
              </p>
            </div>
          </div>
          <p className="text-xl font-bold text-primary tracking-tight">
            {formatCurrency(finalQuotedPrice)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LineItemsBreakdown;
